<script setup lang="ts">
const modalStore = useModalStore()
const config = useRuntimeConfig()
const userStore = useUserStore()
const router = useRouter()
const localePath = useLocalePath()
const cacheStore = useCacheStore()

// Search state - restore from cache if available
const searchTerm = ref('')
const searchResults = ref<any[]>(cacheStore.getCache('searchModal_results') || [])
const isLoading = ref(false)
const debouncedSearchTerm = ref(cacheStore.getCache('searchModal_debouncedTerm') || '')
const debounceTimeout = ref<NodeJS.Timeout | null>(null)
const currentAbortController = ref<AbortController | null>(null)
const wasAborted = ref(false)

// Restore search term from cache if available
onMounted(() => {
  const cachedSearchTerm = cacheStore.getCache('searchModal_searchTerm')
  if (cachedSearchTerm) {
    searchTerm.value = cachedSearchTerm
  }
})

// Save search term to cache
watch(searchTerm, (newTerm) => {
  cacheStore.setCache('searchModal_searchTerm', newTerm)
})

// Debounce search term to avoid too many API calls
watch(searchTerm, (newTerm) => {
  if (newTerm.length >= 3) {
    // Debounce the search
    if (debounceTimeout.value) {
      clearTimeout(debounceTimeout.value)
    }
    debounceTimeout.value = setTimeout(() => {
      debouncedSearchTerm.value = newTerm
    }, 350)
  }
  else {
    // searchResults.value = []
    // isLoading.value = false
  }
})

// Save debounced search term to cache
watch(debouncedSearchTerm, (newTerm) => {
  cacheStore.setCache('searchModal_debouncedTerm', newTerm)
})

// Save search results to cache
watch(searchResults, (newResults) => {
  cacheStore.setCache('searchModal_results', newResults)
})

// Watch for debounced search term changes
watch(debouncedSearchTerm, async (newTerm) => {
  if (newTerm && newTerm.length >= 3) {
    isLoading.value = true
    await performSearch(newTerm)
  }
})

// Perform the search
async function performSearch(term: string) {
  if (!term || term.length < 3)
    return

  // Cancel any ongoing request
  if (currentAbortController.value) {
    currentAbortController.value.abort()
  }

  // Create new AbortController for this request
  currentAbortController.value = new AbortController()
  wasAborted.value = false

  // Set loading state when starting the actual API request
  isLoading.value = true

  try {
    // Prepare query parameters
    const queryParams: any = {
      q: term,
      t: 'sugar',
    }

    // Add user stores if user is connected and has stores
    if (userStore.user && userStore.user.stores && userStore.user.stores.length > 0) {
      // Stores contains an array of slugs directly
      queryParams.stores = userStore.user.stores.join(',')
      console.warn('Search with user stores:', queryParams.stores)
    }
    else {
      console.warn('Search without user stores - user not connected or no stores')
    }

    const response = await $fetch(`${config.public.apiUrl}/search/global`, {
      method: 'GET',
      query: queryParams,
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
      signal: currentAbortController.value.signal,
    })

    // Transform the response into groups for UCommandPalette
    searchResults.value = transformSearchResults(response as any)
    if ((response as any).success === 1) {
      isLoading.value = false
    }
  }
  catch {
    // Check if request was aborted
    // console.log('catch')
    /* if (error.name === 'AbortError') {
      wasAborted.value = true
    }
    else {
      console.warning('Search error:', error)
      // searchResults.value = []
    } */
  }
}

// Transform API response into UCommandPalette groups
function transformSearchResults(response: any) {
  const groups = []

  // Handle flyers
  if (response.results?.flyers?.hits && response.results.flyers.hits.length > 0) {
    const flyerItems = response.results.flyers.hits.map((flyer: any) => ({
      result: flyer,
      onSelect: () => {
        router.push(localePath({
          name: 'FlyerPage',
          params: { id: flyer.id, slug: flyer.flyer_slug },
        }))
        modalStore.showSearchModal = false
      },
    }))

    // Add "Voir plus" button
    flyerItems.push({
      result: { isViewMore: true, type: 'flyers' },
      onSelect: () => {
        router.push(localePath({ name: 'FlyersPage' }))
        modalStore.showSearchModal = false
      },
    })

    groups.push({
      id: '01-flyers',
      slot: 'flyers' as const,
      label: 'Circulaires',
      ignoreFilter: true,
      items: flyerItems,
    })
  }

  // Handle flyer items
  if (response.results?.flyer_items?.hits && response.results.flyer_items.hits.length > 0) {
    const itemItems = response.results.flyer_items.hits.map((item: any) => ({
      result: item,
      onSelect: () => {
        modalStore.openItemModal(item.id)
      },
    }))

    // Add "Voir plus" button
    itemItems.push({
      result: { isViewMore: true, type: 'flyer_items' },
      onSelect: () => {
        router.push(localePath({
          name: 'FlyerItemsSearchResultsPage',
          query: { q: debouncedSearchTerm.value },
        }))
        modalStore.showSearchModal = false
      },
    })

    groups.push({
      id: '02-flyer_items',
      slot: 'flyer_items' as const,
      label: 'Produits des circulaires',
      ignoreFilter: true,
      items: itemItems,
    })
  }

  // Handle recipes
  if (response.results?.recipes?.hits && response.results.recipes.hits.length > 0) {
    const recipeItems = response.results.recipes.hits.map((recipe: any) => ({
      result: recipe,
      onSelect: () => {
        router.push(localePath({
          name: 'RecipePage',
          params: { id: recipe.id, slug: recipe.slug },
        }))
        modalStore.showSearchModal = false
      },
    }))

    // Add "Voir plus" button
    recipeItems.push({
      result: { isViewMore: response.results.recipes.total_hits > 5, type: 'recipes' },
      onSelect: () => {
        router.push(localePath({
          name: 'RecipesSearchResultsPage',
          query: { q: debouncedSearchTerm.value },
        }))
        modalStore.showSearchModal = false
      },
    })

    groups.push({
      id: '03-recipes',
      slot: 'recipes' as const,
      label: 'Recettes',
      ignoreFilter: true,
      items: recipeItems,
    })
  }

  // Sort groups to ensure correct order
  return groups.sort((a, b) => {
    if (a.id === '01-flyers')
      return -1
    if (b.id === '01-flyers')
      return 1
    if (a.id === '02-flyer_items')
      return -1
    if (b.id === '02-flyer_items')
      return 1
    return 0
  })
}

// Handle item selection
function handleItemSelect(item: any) {
  if (item.onSelect) {
    item.onSelect()
  }
}

// Clear search function
function clearSearch() {
  searchTerm.value = ''
  searchResults.value = []
  isLoading.value = false

  // Clear cache
  cacheStore.clearCache('searchModal_searchTerm')
  cacheStore.clearCache('searchModal_debouncedTerm')
  cacheStore.clearCache('searchModal_results')

  // Cancel any ongoing request
  if (currentAbortController.value) {
    currentAbortController.value.abort()
    currentAbortController.value = null
  }

  // Clear debounce timeout
  if (debounceTimeout.value) {
    clearTimeout(debounceTimeout.value)
    debounceTimeout.value = null
  }
}

// Calculate flyer status
function getFlyerStatus(flyer: any) {
  const now = new Date()
  const startDate = new Date(flyer.start_date)
  const endDate = new Date(flyer.end_date)

  if (now < startDate) {
    const diffTime = startDate.getTime() - now.getTime()
    const diffHours = Math.ceil(diffTime / (1000 * 60 * 60))
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffHours < 24) {
      return `Débute dans ${diffHours} heure${diffHours > 1 ? 's' : ''}`
    }
    else {
      return `Débute dans ${diffDays} jour${diffDays > 1 ? 's' : ''}`
    }
  }
  else if (now <= endDate) {
    const diffTime = endDate.getTime() - now.getTime()
    const diffHours = Math.ceil(diffTime / (1000 * 60 * 60))
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffHours < 24) {
      return `Se termine dans ${diffHours} heure${diffHours > 1 ? 's' : ''}`
    }
    else {
      return `Se termine dans ${diffDays} jour${diffDays > 1 ? 's' : ''}`
    }
  }
  else {
    const diffTime = now.getTime() - endDate.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return `Terminé depuis ${diffDays} jour${diffDays > 1 ? 's' : ''}`
  }
}

// Get flyer status color class
function getFlyerStatusColor(flyer: any) {
  const now = new Date()
  const startDate = new Date(flyer.start_date)
  const endDate = new Date(flyer.end_date)

  if (now >= startDate && now <= endDate) {
    return 'text-green-600' // En vigueur - vert
  }
  else {
    return 'text-yellow-600' // Pas encore en vigueur ou terminé - jaune foncé
  }
}

// Cleanup on unmount
onUnmounted(() => {
  if (debounceTimeout.value) {
    clearTimeout(debounceTimeout.value)
    debounceTimeout.value = null
  }
  if (currentAbortController.value) {
    currentAbortController.value.abort()
    currentAbortController.value = null
  }
})
</script>

<template>
  <UModal
    v-model:open="modalStore.showSearchModal"
    class="search-modal-mobile"
  >
    <template #content>
      <UCommandPalette
        v-model:search-term="searchTerm"
        :ui="{ label: 'text-sm' }"
        icon="i-ic-sharp-search"
        close
        :groups="searchResults"
        :loading="isLoading"
        placeholder="Rechercher..."
        class="search-modal-xl"
        @update:open="modalStore.showSearchModal = $event"
        @update:model-value="handleItemSelect"
      >
        <template #empty>
          <div class="text-center py-6">
            <UIcon name="i-ic-sharp-search" class="mx-auto h-12 w-12 text-gray-400" />
            <h3 class="mt-2 text-sm font-medium text-gray-900">
              Rechercher des circulaires, produits ou recettes
            </h3>
            <p class="text-sm text-gray-500">
              {{ searchTerm.length < 3 ? 'Entrez au moins 3 caractères pour débuter...' : 'Aucun résultat trouvé!' }}
            </p>
          </div>

          <!-- Warning for non-logged users -->
          <div v-if="(!userStore.user || !userStore.user.stores || userStore.user.stores.length === 0)" class="p-4">
            <UAlert

              color="secondary"
              variant="soft"
              icon="i-mdi-information"
              class=" text-left"
              description="Pour inclure les rabais selon vos commerces favoris aux résultats, connectez-vous à votre compte Glouton."
              :actions="[
                {
                  label: 'Connexion',
                  color: 'secondary',
                  variant: 'outline',
                  onClick: () => modalStore.openLoginModal(),
                },
                {
                  label: 'Créer un compte',
                  color: 'secondary',
                  variant: 'soft',
                  to: localePath({ name: 'RegisterPage' }),
                },
              ]"
            >
              <strong>Connectez-vous gratuitement</strong> pour voir les rabais personnalisés selon vos magasins préférés !
            </UAlert>
          </div>
        </template>

        <template #close>
          <div class="flex items-center justify-center space-x-2">
            <UButton v-if="searchTerm.length >= 1" variant="ghost" color="neutral" size="sm" icon="i-mdi-close" @click="clearSearch" />
            <UButton label="Fermer" color="primary" size="sm" class="" @click="modalStore.showSearchModal = false" />
          </div>
        </template>
        <template #flyer_items="{ item }">
          <ULink v-if="item.result.isViewMore" class="flex items-center justify-center py-2 h-4" :to="localePath({ name: 'FlyerItemsSearchResultsPage', query: { q: debouncedSearchTerm } })">
            <UIcon name="i-lucide-arrow-right" class="size-4 mr-1 text-secondary" />
            <span class="text-sm font-medium text-secondary">Plus de résultats...</span>
          </ULink>
          <div v-else class="flex items-center gap-2 truncate">
            <UAvatar
              :ui="{ root: 'rounded border-1 border-gray-300' }" :src="item.result.cdn_thumbnail_url" icon="i-lucide-image" size="xl"
            />
            <div class="flex flex-col truncate">
              <div class="text-poivre text-left first-letter-uppercase truncate">
                <span v-html="item.result._formatted.item_label" /> <span v-if="item.result.item_description" class="text-tomate-400 font-bold mx-0.5">&middot;</span> <span v-if="item.result.item_description" class="text-poivre-400">{{ item.result.item_description }}</span>
              </div>
              <div class=" text-left flex items-center space-x-1.5">
                <img :src="`${config.public.backendCdnUrl}/u/stores/${item.result.store_slug}.png`" class="h-3.5"> <span v-if="item.result.ing_score > 0 && userStore.user && userStore.user.extra" class="text-xs text-white rounded-full px-1" :style="{ backgroundColor: item.result.colors.star_color }">{{ item.result.ing_score }}</span> <span class="text-xs text-tomate-600 font-semibold">{{ item.result.price_description }}</span>
              </div>
            </div>
          </div>
        </template>
        <template #flyers="{ item }">
          <div v-if="item.result.isViewMore" class="flex items-center justify-between py-2 h-4">
            <UIcon name="i-lucide-arrow-right" class="size-4 mr-1 text-secondary" />
            <span class="text-xs font-medium text-secondary">Voir toutes les circulaires...</span>
          </div>
          <div v-else class="flex items-center gap-2">
            <UAvatar :ui="{ root: 'rounded' }" :src="item.result.flyer_thumb" icon="i-lucide-image" size="xl" />
            <div class="flex flex-col justify-between h-8">
              <div class="text-left">
                <img :src="`${config.public.backendCdnUrl}/u/stores/${item.result.store_slug}.png`" class="h-5">
              </div>
              <div class="text-xs text-left" :class="getFlyerStatusColor(item.result)">
                {{ getFlyerStatus(item.result) }}
              </div>
            </div>
          </div>
        </template>
        <template #recipes="{ item }">
          <ULink v-if="item.result.isViewMore" class="flex items-center justify-center py-2 h-4" :to="localePath({ name: 'RecipesSearchResultsPage', query: { q: debouncedSearchTerm } })">
            <UIcon name="i-lucide-arrow-right" class="size-4 mr-1 text-secondary" />
            <span class="text-sm font-medium text-secondary">Plus de résultats...</span>
          </ULink>
          <div v-else class="flex items-center gap-2 truncate">
            <UAvatar :ui="{ root: 'rounded' }" :src="item.result.thumbnailUrlFull" icon="i-lucide-image" size="xs" />
            <div class="flex flex-col min-w-0 flex-1">
              <div class="text-left truncate">
                <span class="text-poivre" v-html="item.result._formatted.title" /> <span class="text-tomate-400 font-bold mx-2 hidden">&middot;</span> <span class="text-poivre-400 hidden">{{ item.result.website_name }}</span>
              </div>
            </div>
          </div>
        </template>
      </UCommandPalette>
    </template>
  </UModal>
</template>

<style scoped>
.first-letter-uppercase::first-letter {
  text-transform: uppercase;
}

/* Style pour les balises <em> dans les résultats de recherche */
:deep(em) {
  text-decoration: none;
  text-decoration-color: #152b68; /* Couleur orange pour le soulignement */
  text-decoration-thickness: 1px;
  background-color: #f7f6d5;
  border-radius: 0.25rem;
}

/* Make the search input text base size */
.search-modal-xl :deep(input) {
  font-size: 1rem !important; /* text-base equivalent */
  line-height: 1.5rem !important;
}
</style>
